import { Checkbox, DatePicker, Divider, Flex, Form, TimePicker } from "antd"
import { WeekDaysLabels } from "pagesV2/pro/Calendar/constants"
import { useTranslation } from "react-i18next"
import { spacings } from "themes/variables"
import { Text } from "ui/Typography"
import styles from "./index.module.css"
import { endDateValidate, endTimeValidate, startDateValidate, startTimeValidate } from "pagesV2/pro/Calendar/utils"
import PropTypes from "prop-types"
import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
dayjs.extend(customParseFormat)

const WeekDays = ({ form, editingTimeOffItem }) => {
  const { t } = useTranslation()
  const watchedSelectedDays = Form.useWatch("selectedDays", form)
  const selectedDays = Array.isArray(watchedSelectedDays) ? watchedSelectedDays : []
  const [searchParams] = useSearchParams()
  const paramDate = searchParams.get("date")

  useEffect(() => {
    if (!paramDate) return
    const parsed = dayjs(paramDate, ["YYYY-MM-DD", "MM/DD/YYYY"], true)
    if (!parsed.isValid()) return
    const existing = form.getFieldValue("weeksStartDate")
    if (!existing) {
      const today = dayjs().startOf("day")
      const parsedStart = parsed.startOf("day")
      const clampedStart = parsedStart.isBefore(today) ? today : parsedStart
      form.setFieldsValue({ weeksStartDate: clampedStart })
      const existingEnd = form.getFieldValue("weeksEndDate")
      if (existingEnd && dayjs(existingEnd).startOf("day").isBefore(clampedStart)) {
        form.setFieldsValue({ weeksEndDate: clampedStart })
      }
    }
  }, [paramDate, form])

  const handleWeekDayLabelClick = (day) => {
    const next = selectedDays?.includes(day) ? selectedDays?.filter((d) => d !== day) : [...selectedDays, day]
    form.setFieldsValue({ selectedDays: next })
  }

  const sortedSelectedDays = Array.isArray(selectedDays)
    ? [...selectedDays].sort(
        (a, b) => WeekDaysLabels?.findIndex((d) => d?.fullName === a) - WeekDaysLabels?.findIndex((d) => d?.fullName === b)
      )
    : []

  const getStartTimeRules = (isFullDay, day) => {
    return isFullDay
      ? []
      : [
          { required: true, message: "" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              return startTimeValidate(t, getFieldValue(`endTime_${day}`), value)
            }
          })
        ]
  }

  const getEndTimeRules = (isFullDay, day) => {
    return isFullDay
      ? []
      : [
          { required: true, message: "" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              return endTimeValidate(t, getFieldValue(`startTime_${day}`), value)
            }
          })
        ]
  }

  // Normalize any Dayjs/Moment value to a local midnight timestamp safely
  const toDayStartTs = (d) => {
    if (!d) return null
    try {
      const y = typeof d.year === "function" ? d.year() : null
      const m = typeof d.month === "function" ? d.month() : null
      const dd = typeof d.date === "function" ? d.date() : null
      if (typeof y === "number" && typeof m === "number" && typeof dd === "number") {
        return new Date(y, m, dd).getTime()
      }
      const asDayjs = dayjs(d)
      return asDayjs.isValid() ? asDayjs.startOf("day").valueOf() : null
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const disablePastDates = (current) => {
    const curTs = toDayStartTs(current)
    if (curTs == null) return false
    const todayTs = toDayStartTs(dayjs())
    return curTs < todayTs
  }

  const handleStartDateChange = (next) => {
    // If From is cleared, clear To as well
    if (!next) {
      form.setFieldsValue({ weeksEndDate: null })
      return
    }
    const nextStart = dayjs(next).startOf("day")
    const end = form.getFieldValue("weeksEndDate")
    if (end && dayjs(end).startOf("day").isBefore(nextStart)) {
      form.setFieldsValue({ weeksEndDate: nextStart })
    }
  }

  const handleEndDateChange = (next) => {
    if (!next) return
    const start = form.getFieldValue("weeksStartDate")
    if (!start) return
    const startTs = toDayStartTs(start)
    const endTs = toDayStartTs(next)
    if (endTs != null && startTs != null && endTs < startTs) {
      // Clamp end to start if a prior date is manually typed/selected
      form.setFieldsValue({ weeksEndDate: dayjs(start).startOf("day") })
    }
  }

  return (
    <Flex vertical gap={spacings.M}>
      <Text fw={500}>{t("calendar.schedule_time_off.select_weekday")}</Text>
      <Form.Item
        name="selectedDays"
        rules={[{ required: true, type: "array", min: 1, message: t("calendar.schedule_time_off.select_at_least_one_day") }]}
        style={{ marginBottom: 0 }}
        getValueFromEvent={() => form.getFieldValue("selectedDays")}
      >
        <Flex align="center" gap={spacings.XXS} className="overflow-auto py-s">
          {WeekDaysLabels.map((day) => {
            const value = day?.fullName
            const checked = selectedDays.includes(value)
            const isDisabled = !!editingTimeOffItem
            return (
              <Flex
                justify="center"
                align="center"
                vertical
                gap={spacings.XS}
                key={value}
                onClick={(e) => {
                  e.stopPropagation()
                  !isDisabled && handleWeekDayLabelClick(value)
                }}
              >
                <Text className={styles.weekDaysLabels} fw={500}>
                  {day.shortName}
                </Text>
                <Checkbox
                  checked={checked}
                  onChange={() => !isDisabled && handleWeekDayLabelClick(value)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={isDisabled}
                />
              </Flex>
            )
          })}
        </Flex>
      </Form.Item>
      <Flex vertical>
        {sortedSelectedDays?.map((day) => (
          <Form.Item key={day} shouldUpdate={(prev, curr) => prev[`fullDay_${day}`] !== curr[`fullDay_${day}`]} noStyle>
            {() => {
              const isFullDay = form.getFieldValue(`fullDay_${day}`)
              return (
                <Flex vertical wrap="wrap" className="mt-l">
                  <Text fw={500}>{`${t("calendar.schedule_time_off.start_and_end_times", { day })}* :`}</Text>
                  <Flex gap={spacings.S} align="center" className="mt-xs" wrap="wrap">
                    <Form.Item name={`startTime_${day}`} noStyle rules={getStartTimeRules(isFullDay, day)}>
                      <TimePicker
                        use12Hours
                        format="hh:mm a"
                        placeholder="Start Time"
                        style={{ width: "150px" }}
                        needConfirm={false}
                        disabled={isFullDay}
                      />
                    </Form.Item>
                    <Text>-</Text>
                    <Form.Item name={`endTime_${day}`} noStyle rules={getEndTimeRules(isFullDay, day)}>
                      <TimePicker
                        use12Hours
                        format="hh:mm a"
                        placeholder="End Time"
                        style={{ width: "150px" }}
                        needConfirm={false}
                        disabled={isFullDay}
                      />
                    </Form.Item>
                    <Flex align="center" gap={spacings.XS}>
                      <Form.Item name={`fullDay_${day}`} noStyle valuePropName="checked">
                        <Checkbox
                          onChange={(e) => {
                            if (e.target?.checked) {
                              form.setFieldsValue({ [`startTime_${day}`]: null, [`endTime_${day}`]: null })
                            }
                          }}
                        />
                      </Form.Item>
                      <Text>{t("calendar.schedule_time_off.full_day")}</Text>
                    </Flex>
                  </Flex>
                </Flex>
              )
            }}
          </Form.Item>
        ))}
      </Flex>
      <Divider />
      <Flex vertical gap={spacings.M}>
        <Text fw={500}>{t("calendar.schedule_time_off.select_start_and_end_dates")}</Text>
        <Flex gap={spacings.S} justify="space-between">
          <Flex vertical gap={spacings.XXS} className="w-full">
            <Text fw={500}>{t("calendar.schedule_time_off.from")}</Text>
            <Form.Item
              name="weeksStartDate"
              rules={[
                { required: true, message: t("calendar.schedule_time_off.start_date_is_required") },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    return startDateValidate(t, getFieldValue("weeksEndDate"), value)
                  }
                })
              ]}
            >
              <DatePicker
                className="w-full"
                placeholder="MM/DD/YYYY"
                format="MM/DD/YYYY"
                disabledDate={disablePastDates}
                onChange={handleStartDateChange}
              />
            </Form.Item>
          </Flex>
          <Flex vertical gap={spacings.XXS} className="w-full">
            <Text fw={500}>{`${t("calendar.schedule_time_off.to")}*`}</Text>
            <Form.Item shouldUpdate={(prev, curr) => prev.weeksStartDate !== curr.weeksStartDate} noStyle>
              {({ getFieldValue }) => {
                const start = getFieldValue("weeksStartDate")
                const disabledEnd = (current) => {
                  const curTs = toDayStartTs(current)
                  if (curTs == null) return false
                  const startTs = toDayStartTs(start)
                  if (startTs != null) return curTs < startTs
                  return disablePastDates(current)
                }
                return (
                  <Form.Item
                    name="weeksEndDate"
                    rules={[
                      { required: true, message: t("calendar.schedule_time_off.end_date_is_required") },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          return endDateValidate(t, getFieldValue("weeksStartDate"), value)
                        }
                      })
                    ]}
                  >
                    <DatePicker
                      className="w-full"
                      placeholder="MM/DD/YYYY"
                      format="MM/DD/YYYY"
                      disabledDate={disabledEnd}
                      disabled={!start}
                      onChange={handleEndDateChange}
                    />
                  </Form.Item>
                )
              }}
            </Form.Item>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

WeekDays.propTypes = {
  form: PropTypes.object.isRequired,
  editingTimeOffItem: PropTypes.object
}

export default WeekDays
